import React from "react";
import { useTranslation } from "react-i18next";
import {
  FinancialAssistanceTitle,
  UpfrontPaymentInputRowWrapper,
  UpfrontPaymentInputTitle,
  UpfrontPaymentInputWrapper,
  UpfrontPaymentTextInputComponent,
} from "../UpfrontPayment.styles";
import { StyledUISelect } from "../../shared/UI-Library/StyledUISelect";
import StyledMUIMultiSelect from "../../shared/UI-Library/StyledMUISelect";
import { ExtenuatingCircumstances } from "../types";
import { StyledInputOptional } from "../../shared/UI-Library/UI-Library.styles";

interface OptionalQuestionsProps {
  localities: string[];
  optionalQuestions: {
    locality?: string;
    extenuatingCircumstances: string[];
    otherCircumstance?: string;
  };
  setOptionalQuestions: React.Dispatch<
    React.SetStateAction<{
      locality?: string;
      extenuatingCircumstances: string[];
      otherCircumstance?: string;
    }>
  >;
}

export const StepOptionalQuestions: React.FC<OptionalQuestionsProps> = ({
  localities,
  optionalQuestions,
  setOptionalQuestions,
}) => {
  const { t } = useTranslation();
  const handleLocalityChange = (
    event: React.ChangeEvent<HTMLSelectElement>,
  ) => {
    setOptionalQuestions({
      ...optionalQuestions,
      locality: event.target.value,
    });
  };

  const handleExtenuatingCircumstancesChange = (values: string[]) => {
    setOptionalQuestions({
      ...optionalQuestions,
      extenuatingCircumstances: values,
    });
  };

  const handleOtherCircumstanceChange = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setOptionalQuestions({
      ...optionalQuestions,
      otherCircumstance: event.target.value,
    });
  };

  return (
    <>
      <FinancialAssistanceTitle>
        {t("upfrontPayment.financialAssistance.additionalInformation")}{" "}
        <StyledInputOptional style={{ fontWeight: "bold" }}>
          {t("upfrontPayment.stripe.optional")}
        </StyledInputOptional>
      </FinancialAssistanceTitle>
      <StyledUISelect
        fieldTitle={t("upfrontPayment.financialAssistance.localityQuestion")}
        value={optionalQuestions.locality || ""}
        onChange={handleLocalityChange}
        data-testid="locality-select"
      >
        <option value="" disabled>
          {t("upfrontPayment.financialAssistance.selectArea")}
        </option>
        {localities
          .sort((a, b) => a.localeCompare(b))
          .map((locality) => (
            <option key={locality} value={locality}>
              {locality}
            </option>
          ))}
      </StyledUISelect>

      <StyledMUIMultiSelect
        fieldTitle={t(
          "upfrontPayment.financialAssistance.extenuatingCircumstancesQuestion",
        )}
        options={ExtenuatingCircumstances.map((circumstance) => ({
          label: t(
            `upfrontPayment.financialAssistance.extenuatingCircumstances.${circumstance}`,
          ),
          value: circumstance,
        }))}
        selectedValues={optionalQuestions.extenuatingCircumstances || []}
        customOnChange={handleExtenuatingCircumstancesChange}
        data-testid="extenuating-circumstances-select"
      />
      {optionalQuestions.extenuatingCircumstances.includes("Other") && (
        <UpfrontPaymentInputRowWrapper>
          <UpfrontPaymentInputWrapper>
            <UpfrontPaymentInputTitle>
              {t(
                "upfrontPayment.financialAssistance.extenuatingCircumstances.Other",
              )}
            </UpfrontPaymentInputTitle>
            <UpfrontPaymentTextInputComponent
              value={optionalQuestions.otherCircumstance || undefined}
              onChange={handleOtherCircumstanceChange}
              disableUnderline
              data-testid="other-circumstance-input"
            />
          </UpfrontPaymentInputWrapper>
        </UpfrontPaymentInputRowWrapper>
      )}
    </>
  );
};
